import React, {Component} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import Image from 'react-bootstrap/Image';
import Card from 'react-bootstrap/Card';
import i18n from 'i18next';

import {
  formatPlaceLabel, handleTravelImageError, travelAirportLabel, travelCityLabel,
} from '../utils/travelDisplay.js';
import './TravelItem.css';

const VIEW_MODES = ['grid', 'short'];

class TravelItem extends Component {
  handleActivate = () => {
    this.props.onActivate(this.props.travel);
  };

  stopPropagation = (e) => {
    e.stopPropagation();
  };

  handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      this.handleActivate();
    }
  };

  render() {
    const t = i18n.t.bind(i18n);
    const { travel, isSelected, lngPrefix = '', viewMode = 'grid' } = this.props;

    if (travel === undefined) {
      return null;
    }

    if (!travel.city && !travel.destination) {
      console.log("Weird record: ", travel);
      return null;
    }

    const country = travel.country || '';
    const city = travelCityLabel(travel);
    const airportName = travelAirportLabel(travel);
    const placeLabel = formatPlaceLabel(city, country);
    const image = travel.image_big;
    const departDate = moment(travel.depart_date).format('DD/MM/YYYY');
    const returnDate = moment(travel.return_date).format('DD/MM/YYYY');
    const calStart = moment(travel.depart_date).format('YYYY-MM-DD');
    const days = moment(travel.return_date).diff(moment(travel.depart_date), 'days');
    const priceStr = `$${travel.value}`;
    const urlFrom = `${lngPrefix}/from/${travel.airport.code}/since/${calStart}`;
    const airportCode = travel.airport?.code || '';

    const cardClass = [
      'ticket',
      'card',
      'travel-item-card',
      `travel-item-card--${viewMode}`,
      isSelected ? 'travel-item-card--selected' : '',
    ].filter(Boolean).join(' ');

    const cardProps = {
      className: cardClass,
      id: travel.id,
      ref: travel.ref,
      role: 'button',
      tabIndex: 0,
      'aria-pressed': isSelected,
      onClick: this.handleActivate,
      onKeyDown: this.handleKeyDown,
    };

    if (viewMode === 'short') {
      return this.renderShort(cardProps, {
        t, placeLabel, image, departDate, returnDate, days, priceStr, urlFrom, airportName,
      });
    }
    return this.renderGrid(cardProps, {
      t, placeLabel, image, departDate, returnDate, days, priceStr, urlFrom, airportName, airportCode,
    });
  }

  renderShort(cardProps, ctx) {
    const { placeLabel, image, departDate, returnDate, days, priceStr } = ctx;
    return (
      <div className="travel-list-item travel-list-item--short">
        <Card {...cardProps}>
          <div className="travel-item-card__short-row">
            <Image src={image} className="travel-item-card__thumb" alt={placeLabel}
                   onError={handleTravelImageError} />
            <div className="travel-item-card__short-main">
              <div className="travel-item-card__short-title">{placeLabel}</div>
              <div className="travel-item-card__short-meta">
                <span>{ctx.t('travelCard.duration', { count: days })}</span>
                <span className="travel-item-card__short-sep" aria-hidden="true">·</span>
                <span>{departDate} → {returnDate}</span>
              </div>
            </div>
            <span className="travel-item-card__short-price">{priceStr}</span>
          </div>
        </Card>
      </div>
    );
  }

  renderGrid(cardProps, ctx) {
    const {
      t, placeLabel, image, departDate, returnDate, days, priceStr, urlFrom, airportName, airportCode,
    } = ctx;
    return (
      <div className="travel-list-item travel-list-item--grid">
        <Card {...cardProps}>
          <Image src={image} className="card-img-top card-image travel-item-card__image"
                 alt={placeLabel} onError={handleTravelImageError} />
          <div className="travel-item-card__meta">
            <span className="travel-item-card__dates">{departDate} → {returnDate}</span>
            <span className="travel-item-card__pill">{t('travelCard.duration', { count: days })}</span>
          </div>
          <Card.Body className="travel-item-card__body">
            <div className="travel-item-card__main">
              <div className="travel-item-card__place-block">
                <div className="travel-item-card__place">{placeLabel}</div>
                <a
                  className="travel-item-card__alt-origin"
                  href={urlFrom}
                  onClick={this.stopPropagation}
                  aria-label={t('travelCard.dealsFromCodeAria', {
                    airport: airportName,
                    code: airportCode,
                  })}
                  title={t('travelCard.dealsFromCodeAria', {
                    airport: airportName,
                    code: airportCode,
                  })}
                >
                  {t('travelCard.dealsFromCode', { code: airportCode })}
                </a>
              </div>
              <div className="travel-item-card__price-block"
                   aria-label={t('travelCard.priceAria', { price: priceStr })}>
                <div className="travel-item-card__price-stack">
                  <span className="travel-item-card__price">{priceStr}</span>
                  <span className="travel-item-card__price-hint">{t('travelCard.priceHint')}</span>
                </div>
              </div>
            </div>
          </Card.Body>
        </Card>
      </div>
    );
  }

}

TravelItem.propTypes = {
  travel: PropTypes.object.isRequired,
  isSelected: PropTypes.bool,
  onActivate: PropTypes.func.isRequired,
  lngPrefix: PropTypes.string,
  viewMode: PropTypes.oneOf(VIEW_MODES),
};

export default TravelItem;
